<!-- Floating chat button -->
@if (isEnabled) {
  @if (!isOpen) {
    <button
      class="chat-widget-toggle"
      (click)="toggle()"
      [attr.data-tooltip]="hasUnreadPrompt ? chatTooltipNewMessage : chatTooltipGreeting"
      aria-label="Open travel assistant"
      i18n-aria-label="@@chatOpenAssistant"
    >
      <span class="chat-widget-toggle__icon-wrap">
        <img
          src="https://images.sabscorp.com/images/enterprise/ai-assistant-icon.png"
          alt=""
          class="chat-widget-toggle__icon"
        />
      </span>
      @if (hasUnreadPrompt) {
        <span
          class="chat-widget-toggle__badge"
          aria-label="New message"
          i18n-aria-label="@@chatLauncherBadgeNewMessage"
        ></span>
      }
    </button>
  }

  <!-- Chat panel -->
  @if (isOpen) {
    <div class="chat-widget-panel">
      <!-- Header -->
      <div class="chat-widget-header">
        <div class="chat-widget-header__title">
          <div class="chat-widget-header__icon">
            <img
              src="https://images.sabscorp.com/images/enterprise/ai-assistant-icon.png"
              alt=""
              class="chat-widget-header__icon-img"
            />
          </div>
          <span i18n="@@chatWidgetTitle">Travel Assistant</span>
        </div>
        <div class="chat-widget-header__actions">
          @if (messages.length > 0) {
            <button
              class="chat-widget-header__btn"
              (click)="startNewConversation()"
              (mouseenter)="showTooltip($event, newChatLabel)"
              (mouseleave)="hideTooltip()"
              [attr.aria-label]="newChatLabel"
            >
              <fa-icon [icon]="faPlus"></fa-icon>
            </button>
          }
          <button
            class="chat-widget-header__btn"
            (click)="close()"
            (mouseenter)="showTooltip($event, closeChatLabel)"
            (mouseleave)="hideTooltip()"
            [attr.aria-label]="closeChatLabel"
          >
            <fa-icon [icon]="faXmark"></fa-icon>
          </button>
        </div>
      </div>

      <!-- Messages -->
      <div class="chat-widget-messages" #messageList>
        <!-- Welcome message if empty -->
        @if (messages.length === 0 && !selectedFaq) {
          <div class="chat-widget-welcome">
            <p>
              Hi{{ userForename ? ' ' + userForename : '' }}! How can I assist you with your travel
              plans today?
            </p>
          </div>
        }

        <!-- ENT-16320 — FAQ landing list (browse and select, no AI) -->
        @if (messages.length === 0 && !selectedFaq && faqs.length) {
          <div class="chat-widget-faq">
            <div class="chat-widget-faq__head">
              <span class="chat-widget-faq__title" i18n="@@chatFaqTitle">Frequently asked</span>
              <span class="chat-widget-faq__count">{{ faqs.length }}</span>
            </div>
            @for (faq of visibleFaqs; track faq.id) {
              <button type="button" class="chat-widget-faq__row" (click)="openFaq(faq)">
                <span class="chat-widget-faq__q">{{ faq.question }}</span>
                <fa-icon [icon]="faChevronRight" class="chat-widget-faq__chev"></fa-icon>
              </button>
            }
            @if (faqs.length > faqPreviewCount) {
              <button type="button" class="chat-widget-faq__more" (click)="toggleFaqExpanded()">
                @if (!faqExpanded) {
                  <ng-container i18n="@@chatFaqViewMore"
                    >View more ({{ hiddenFaqCount }})</ng-container
                  >
                } @else {
                  <ng-container i18n="@@chatFaqShowFewer">Show fewer</ng-container>
                }
              </button>
            }
          </div>
        }

        <!-- ENT-16320 — FAQ detail (question header + answer + Back) -->
        @if (selectedFaq) {
          <div class="chat-widget-faq-detail">
            <button type="button" class="chat-widget-faq-detail__back" (click)="closeFaq()">
              <fa-icon [icon]="faArrowLeft"></fa-icon>
              <span i18n="@@chatFaqBack">Back</span>
            </button>
            <h3 class="chat-widget-faq-detail__q">{{ selectedFaq.question }}</h3>
            <div class="chat-widget-faq-detail__a" [innerHTML]="selectedFaqAnswerHtml"></div>
          </div>
        }

        <!-- Message list -->
        @for (msg of messages; track msg.id; let isLastMessage = $last) {
          <div
            class="chat-widget-message"
            [class.chat-widget-message--user]="msg.role === 'user'"
            [class.chat-widget-message--assistant]="msg.role === 'assistant'"
            [class.chat-widget-message--rail]="
              !!msg.railResults || !!msg.flightResults || !!msg.eurostarResults
            "
            [class.chat-widget-message--mi]="msg.state === 'AWAITING_MI' && !!msg.mi"
          >
            <div class="chat-widget-message__bubble-wrap">
              @if (!msg.bookingSummary && !msg.bookingResult) {
                @if (isSameAddressQuestion(msg)) {
                  <div class="chat-widget-message__bubble">
                    {{ msg.text }}
                  </div>
                  @if (msg.id) {
                    <div class="chat-widget-same-address-actions">
                      <button
                        type="button"
                        class="chat-widget-same-address-actions__button"
                        [disabled]="sameAddressChoices.has(msg.id)"
                        (click)="chooseSameAddress(msg, true)"
                      >
                        Same location
                      </button>
                      <button
                        type="button"
                        class="chat-widget-same-address-actions__button"
                        [disabled]="sameAddressChoices.has(msg.id)"
                        (click)="chooseSameAddress(msg, false)"
                      >
                        Different location
                      </button>
                    </div>
                  }
                } @else if (msg.railSeatReservationPrompt) {
                  <div class="chat-widget-message__bubble chat-widget-rail-seat-prompt">
                    <div>This booking requires seat reserving, so I reserved:</div>
                    <br />
                    <div>Reserved the following seat(s):</div>
                    @if (msg.railSeatReservationPrompt.journeys?.length) {
                      @for (
                        section of railSeatJourneySections(msg.railSeatReservationPrompt.journeys);
                        track section.heading
                      ) {
                        <br />
                        <div>
                          <strong>{{ section.heading }}</strong>
                        </div>
                        @for (journey of section.journeys; track $index) {
                          @if (journey.route) {
                            <div>
                              <strong>Leg {{ $index + 1 }} - {{ journey.route }}</strong>
                            </div>
                          }
                          @for (seat of journey.seats; track seat) {
                            @for (seatLine of railSeatLines(seat); track seatLine) {
                              <div>{{ seatLine }}</div>
                            }
                          }
                        }
                      }
                    } @else {
                      @for (seat of msg.railSeatReservationPrompt.seats; track seat) {
                        @for (seatLine of railSeatLines(seat); track seatLine) {
                          <div>{{ seatLine }}</div>
                        }
                      }
                    }
                  </div>
                  <div class="chat-widget-rail-seat-prompt__actions">
                    <button
                      type="button"
                      class="chat-widget-rail-seat-prompt__continue"
                      [disabled]="railSeatReservationContinues.has(msg.id)"
                      (click)="continueRailSeatReservation(msg)"
                    >
                      Continue
                    </button>
                  </div>
                } @else if (!msg.bookingInfo && (formattedTextMap[$index] || msg.text)) {
                  <div class="chat-widget-message__bubble">
                    <div [innerHTML]="messageHtml($index, msg)"></div>
                    @if (msg.role === 'assistant' && msg.id) {
                      <ng-container
                        *ngTemplateOutlet="feedbackButtons; context: { $implicit: msg }"
                      ></ng-container>
                    }
                  </div>
                } @else if (
                  msg.bookingInfo?.field === 'phoneNumber' && msg.bookingInfo?.applicable
                ) {
                  <!-- No text came back from the model for this turn — show -->
                  <!-- the widget's own question as the bubble's text so the -->
                  <!-- question always reads as part of the conversation. -->
                  @if (phoneNumberPassenger(msg.bookingInfo); as passenger) {
                    <div class="chat-widget-message__bubble">
                      {{ phoneNumberQuestionText(passenger.name) }}
                      @if (msg.role === 'assistant' && msg.id) {
                        <ng-container
                          *ngTemplateOutlet="feedbackButtons; context: { $implicit: msg }"
                        ></ng-container>
                      }
                    </div>
                  }
                } @else if (
                  msg.bookingInfo?.field === 'dateOfBirth' && msg.bookingInfo?.applicable
                ) {
                  @if (dateOfBirthPassenger(msg.bookingInfo); as passenger) {
                    <div class="chat-widget-message__bubble">
                      What is {{ passenger.name }}'s date of birth?
                    </div>
                  }
                } @else if (msg.bookingInfo?.field === 'passport' && msg.bookingInfo?.applicable) {
                  @if (passportPassenger(msg.bookingInfo); as passenger) {
                    <div class="chat-widget-message__bubble">
                      {{ passportQuestionText(passenger.name) }}
                      @if (msg.role === 'assistant' && msg.id) {
                        <ng-container
                          *ngTemplateOutlet="feedbackButtons; context: { $implicit: msg }"
                        ></ng-container>
                      }
                    </div>
                  }
                } @else if (msg.bookingInfo?.field === 'account' && msg.bookingInfo?.applicable) {
                  <div class="chat-widget-message__bubble">
                    Which account would you like to use for this rail booking?
                    @if (msg.role === 'assistant' && msg.id) {
                      <ng-container
                        *ngTemplateOutlet="feedbackButtons; context: { $implicit: msg }"
                      ></ng-container>
                    }
                  </div>
                } @else if (
                  msg.bookingInfo?.field === 'ticketQueue' && msg.bookingInfo?.applicable
                ) {
                  <div class="chat-widget-message__bubble">
                    Which ticket queue would you like to use for this rail booking?
                    @if (msg.role === 'assistant' && msg.id) {
                      <ng-container
                        *ngTemplateOutlet="feedbackButtons; context: { $implicit: msg }"
                      ></ng-container>
                    }
                  </div>
                } @else if (
                  msg.bookingInfo?.field === 'ticketDeliveryOption' && msg.bookingInfo?.applicable
                ) {
                  <div class="chat-widget-message__bubble">
                    How would you like your train tickets delivered?
                    @if (msg.role === 'assistant' && msg.id) {
                      <ng-container
                        *ngTemplateOutlet="feedbackButtons; context: { $implicit: msg }"
                      ></ng-container>
                    }
                  </div>
                } @else if (
                  msg.bookingInfo?.field === 'loyaltyProgramme' && msg.bookingInfo?.applicable
                ) {
                  @if (loyaltyProgrammePassenger(msg.bookingInfo); as passenger) {
                    <div class="chat-widget-message__bubble">
                      {{ loyaltyQuestionText(passenger.name) }}
                      @if (msg.role === 'assistant' && msg.id) {
                        <ng-container
                          *ngTemplateOutlet="feedbackButtons; context: { $implicit: msg }"
                        ></ng-container>
                      }
                    </div>
                  }
                } @else if (
                  msg.bookingInfo?.field === 'mealOptions' && msg.bookingInfo?.applicable
                ) {
                  @if (mealOptionsPassenger(msg.bookingInfo); as passenger) {
                    <div class="chat-widget-message__bubble">
                      {{ mealQuestionText(passenger.name) }}
                      @if (msg.role === 'assistant' && msg.id) {
                        <ng-container
                          *ngTemplateOutlet="feedbackButtons; context: { $implicit: msg }"
                        ></ng-container>
                      }
                    </div>
                  }
                } @else if (
                  msg.bookingInfo?.field === 'specialServiceRequest' && msg.bookingInfo?.applicable
                ) {
                  @if (specialServiceRequestPassenger(msg.bookingInfo); as passenger) {
                    <div class="chat-widget-message__bubble">
                      {{ specialServiceRequestQuestionText(passenger.name) }}
                      @if (msg.role === 'assistant' && msg.id) {
                        <ng-container
                          *ngTemplateOutlet="feedbackButtons; context: { $implicit: msg }"
                        ></ng-container>
                      }
                    </div>
                  }
                } @else if (
                  msg.bookingInfo?.field === 'additionalEmail' && msg.bookingInfo?.applicable
                ) {
                  @if (additionalEmailPassenger(msg.bookingInfo); as passenger) {
                    <div class="chat-widget-message__bubble">
                      {{ additionalEmailQuestionText(passenger.name) }}
                      @if (msg.role === 'assistant' && msg.id) {
                        <ng-container
                          *ngTemplateOutlet="feedbackButtons; context: { $implicit: msg }"
                        ></ng-container>
                      }
                    </div>
                  }
                } @else if (msg.bookingInfo?.field === 'cvv' && msg.bookingInfo?.applicable) {
                  <div class="chat-widget-message__bubble">
                    <span i18n="@@chatCvvPrompt"
                      >Please enter your card verification value (CVV) to complete the
                      payment.</span
                    >
                    @if (msg.role === 'assistant' && msg.id) {
                      <ng-container
                        *ngTemplateOutlet="feedbackButtons; context: { $implicit: msg }"
                      ></ng-container>
                    }
                  </div>
                } @else if (
                  msg.bookingInfo?.field === 'flightDetails' && msg.bookingInfo?.applicable
                ) {
                  <div class="chat-widget-message__bubble">
                    {{ flightDetailsQuestionText() }}
                  </div>
                } @else if (
                  (msg.bookingInfo?.field === 'bookingAddress' ||
                    msg.bookingInfo?.field === 'deliveryAddress' ||
                    msg.bookingInfo?.field === 'collectionAddress') &&
                  msg.bookingInfo?.applicable
                ) {
                  <div class="chat-widget-message__bubble">
                    {{ addressQuestionText(msg.bookingInfo.field) }}
                  </div>
                } @else if (
                  msg.bookingInfo?.field === 'flightNumber' && msg.bookingInfo?.applicable
                ) {
                  <div class="chat-widget-message__bubble">
                    {{ flightNumberQuestionText() }}
                  </div>
                } @else if (
                  msg.bookingInfo?.field === 'driverNotes' && msg.bookingInfo?.applicable
                ) {
                  <div class="chat-widget-message__bubble">
                    {{ driverNotesQuestionText() }}
                  </div>
                } @else if (msg.paymentMethodPrompt) {
                  <!-- No text came back from the model for this turn — show -->
                  <!-- the widget's own question as the bubble's text so the -->
                  <!-- question always reads as part of the conversation. -->
                  <div class="chat-widget-message__bubble">
                    {{ paymentMethodQuestionText(msg.paymentMethodPrompt) }}
                    @if (msg.role === 'assistant' && msg.id) {
                      <ng-container
                        *ngTemplateOutlet="feedbackButtons; context: { $implicit: msg }"
                      ></ng-container>
                    }
                  </div>
                } @else if (msg.approverSelectionPrompt) {
                  <div class="chat-widget-message__bubble">
                    {{ approverSelectionQuestionText(msg.approverSelectionPrompt) }}
                    @if (msg.role === 'assistant' && msg.id) {
                      <ng-container
                        *ngTemplateOutlet="feedbackButtons; context: { $implicit: msg }"
                      ></ng-container>
                    }
                  </div>
                }
              }

              @if (msg.bookingInfo?.field === 'dateOfBirth' && msg.bookingInfo?.applicable) {
                @if (dateOfBirthPassenger(msg.bookingInfo); as passenger) {
                  <app-chat-date-of-birth-input
                    [passengerName]="passenger.name"
                    [savedValue]="passenger.savedValue"
                    [required]="msg.bookingInfo.required"
                    [locked]="!isLastMessage"
                    [showLabel]="false"
                    (answer)="onDateOfBirthAnswer(passenger.name, passenger.id, $event)"
                    (skip)="onDateOfBirthSkip(passenger.name, passenger.id)"
                  ></app-chat-date-of-birth-input>
                }
              }

              <!-- Chatbot phone-number picker widget — rendered as its own -->
              <!-- block outside/below the text bubble (the question itself -->
              <!-- is shown in the bubble above, so the widget's own label is -->
              <!-- suppressed). Stays visible on earlier messages once -->
              <!-- answered/superseded, but locked (read-only) so the user -->
              <!-- can't reopen an old widget instance further up the -->
              <!-- scrollback. -->
              @if (msg.bookingInfo?.field === 'phoneNumber' && msg.bookingInfo?.applicable) {
                @if (phoneNumberPassenger(msg.bookingInfo); as passenger) {
                  <app-chat-phone-input
                    [passengerName]="passenger.name"
                    [savedNumbers]="passenger.savedNumbers ?? []"
                    [required]="msg.bookingInfo.required"
                    [locked]="!isLastMessage"
                    [showLabel]="false"
                    (answer)="onPhoneNumberAnswer(passenger.name, passenger.id, $event)"
                    (skip)="onPhoneNumberSkip(passenger.name, passenger.id)"
                  ></app-chat-phone-input>
                }
              }

              <!-- Chatbot passport picker widget — same outside-bubble, -->
              <!-- locked-history behaviour as the phone-number widget above. -->
              @if (msg.bookingInfo?.field === 'passport' && msg.bookingInfo?.applicable) {
                @if (passportPassenger(msg.bookingInfo); as passenger) {
                  <app-chat-passport-input
                    [passengerName]="passenger.name"
                    [savedPassports]="passenger.savedPassports ?? []"
                    [passportFields]="msg.bookingInfo.passportFields"
                    [required]="msg.bookingInfo.required"
                    [locked]="!isLastMessage"
                    [showLabel]="false"
                    (answer)="onPassportAnswer(passenger.name, passenger.id, $event)"
                    (skip)="onPassportSkip(passenger.name, passenger.id)"
                  ></app-chat-passport-input>
                }
              }

              <!-- Chatbot loyalty-programme picker widget — same -->
              <!-- outside-bubble, locked-history behaviour as the -->
              <!-- phone-number/passport widgets above. -->
              @if (msg.bookingInfo?.field === 'loyaltyProgramme' && msg.bookingInfo?.applicable) {
                @if (loyaltyProgrammePassenger(msg.bookingInfo); as passenger) {
                  <app-chat-loyalty-input
                    [passengerName]="passenger.name"
                    [savedValue]="passenger.savedValue"
                    [required]="msg.bookingInfo.required"
                    [locked]="!isLastMessage"
                    [showLabel]="false"
                    (answer)="onLoyaltyAnswer(passenger.name, passenger.id, $event)"
                    (skip)="onLoyaltySkip(passenger.name, passenger.id)"
                  ></app-chat-loyalty-input>
                }
              }

              <!-- Chatbot meal-option picker widget — same outside-bubble, -->
              <!-- locked-history behaviour as the widgets above. -->
              @if (msg.bookingInfo?.field === 'mealOptions' && msg.bookingInfo?.applicable) {
                @if (mealOptionsPassenger(msg.bookingInfo); as passenger) {
                  <app-chat-choice-input
                    [passengerName]="passenger.name"
                    [choices]="mealChoices(msg.bookingInfo)"
                    [savedValue]="passenger.savedValue"
                    savedValueKey="meal"
                    fieldLabel="Meal option"
                    placeholder="None"
                    [required]="msg.bookingInfo.required"
                    [locked]="!isLastMessage"
                    [showSkip]="false"
                    [showLabel]="false"
                    (answer)="onMealAnswer(passenger.name, passenger.id, $event)"
                    (skip)="onMealSkip(passenger.name, passenger.id)"
                  ></app-chat-choice-input>
                }
              }

              @if (msg.bookingInfo?.field === 'account' && msg.bookingInfo?.applicable) {
                <app-chat-choice-input
                  passengerName=""
                  [choices]="bookingInfoChoices(msg.bookingInfo)"
                  [savedValue]="accountSavedValue(msg.bookingInfo)"
                  savedValueKey="account"
                  fieldLabel="Account"
                  placeholder="Select an account..."
                  [required]="msg.bookingInfo.required"
                  [locked]="!isLastMessage"
                  [showLabel]="false"
                  (answer)="onAccountAnswer($event)"
                ></app-chat-choice-input>
              }

              @if (msg.bookingInfo?.field === 'ticketQueue' && msg.bookingInfo?.applicable) {
                <app-chat-choice-input
                  passengerName=""
                  [choices]="bookingInfoChoices(msg.bookingInfo)"
                  [savedValue]="ticketQueueSavedValue(msg.bookingInfo)"
                  savedValueKey="ticketQueue"
                  fieldLabel="Ticket queue"
                  placeholder="Please select..."
                  emailForValue="eticket"
                  emailLabel="E-ticket delivery email"
                  [defaultEmail]="userEmail"
                  stationForValue="ticketondeparture"
                  stationLabel="Collection station"
                  [defaultStationName]="msg.bookingInfo.outboundStationName"
                  [defaultStationCode]="msg.bookingInfo.outboundStationCode"
                  [required]="true"
                  [locked]="!isLastMessage"
                  [showLabel]="false"
                  (answer)="onTicketQueueAnswer($event)"
                ></app-chat-choice-input>
              }

              @if (
                msg.bookingInfo?.field === 'ticketDeliveryOption' && msg.bookingInfo?.applicable
              ) {
                <app-chat-choice-input
                  passengerName=""
                  [choices]="bookingInfoChoices(msg.bookingInfo)"
                  [savedValue]="ticketDeliveryOptionSavedValue(msg.bookingInfo)"
                  savedValueKey="ticketDeliveryOption"
                  fieldLabel="Ticket delivery option"
                  placeholder="Please select..."
                  emailForValue="eticket"
                  emailLabel="E-ticket delivery email"
                  [defaultEmail]="userEmail"
                  infoForValue="kiosk"
                  [infoMessageStationName]="msg.bookingInfo.outboundStationName"
                  [required]="true"
                  [locked]="!isLastMessage && !isActiveTicketDeliveryOption(msg, $index)"
                  [showLabel]="false"
                  [showSkip]="false"
                  (selectionChange)="onTicketDeliveryOptionSelection()"
                  (answer)="onTicketDeliveryOptionAnswer($event)"
                ></app-chat-choice-input>
              }

              <!-- Chatbot special-service-request picker widget — same -->
              <!-- outside-bubble, locked-history behaviour as the widgets -->
              <!-- above. -->
              @if (
                msg.bookingInfo?.field === 'specialServiceRequest' && msg.bookingInfo?.applicable
              ) {
                @if (specialServiceRequestPassenger(msg.bookingInfo); as passenger) {
                  <app-chat-choice-input
                    [passengerName]="passenger.name"
                    [choices]="specialServiceRequestChoices(msg.bookingInfo)"
                    [savedValue]="passenger.savedValue"
                    savedValueKey="specialNeeds"
                    fieldLabel="Special service request"
                    placeholder="None"
                    [required]="msg.bookingInfo.required"
                    [locked]="!isLastMessage"
                    [showSkip]="false"
                    [showLabel]="false"
                    (answer)="onSpecialServiceRequestAnswer(passenger.name, passenger.id, $event)"
                    (skip)="onSpecialServiceRequestSkip(passenger.name, passenger.id)"
                  ></app-chat-choice-input>
                }
              }

              <!-- Chatbot additional-email picker widget — same -->
              <!-- outside-bubble, locked-history behaviour as the widgets -->
              <!-- above. Up to 3 free-text email fields, no `choices`. -->
              @if (msg.bookingInfo?.field === 'additionalEmail' && msg.bookingInfo?.applicable) {
                @if (additionalEmailPassenger(msg.bookingInfo); as passenger) {
                  <app-chat-email-input
                    [passengerName]="passenger.name"
                    [savedValue]="passenger.savedValue"
                    [required]="msg.bookingInfo.required"
                    [locked]="!isLastMessage"
                    [showLabel]="false"
                    (answer)="onAdditionalEmailAnswer(passenger.name, passenger.id, $event)"
                    (skip)="onAdditionalEmailSkip(passenger.name, passenger.id)"
                  ></app-chat-email-input>
                }
              }

              <!-- CVV input widget for Individual Credit Card payment -->
              @if (msg.bookingInfo?.field === 'cvv' && msg.bookingInfo?.applicable) {
                <app-chat-cvv-input
                  [required]="msg.bookingInfo.required"
                  [locked]="!isLastMessage"
                  [showLabel]="false"
                  (answer)="onCvvAnswer($event)"
                  (skip)="onCvvSkip()"
                ></app-chat-cvv-input>
              }

              @if (msg.bookingInfo?.field === 'flightDetails' && msg.bookingInfo?.applicable) {
                <app-chat-flight-details-input
                  [savedValue]="msg.bookingInfo.savedValue ?? null"
                  [required]="msg.bookingInfo.required"
                  [locked]="!isLastMessage"
                  (answer)="onFlightDetailsAnswer($event)"
                  (skip)="onFlightDetailsSkip()"
                ></app-chat-flight-details-input>
              }

              @if (msg.bookingInfo?.field === 'carDetails' && msg.bookingInfo?.applicable) {
                <app-chat-car-details-input
                  [savedValue]="msg.bookingInfo.savedValue ?? null"
                  [required]="msg.bookingInfo.required"
                  [locked]="!isLastMessage"
                  (answer)="onCarDetailsAnswer($event)"
                  (skip)="onCarDetailsSkip()"
                ></app-chat-car-details-input>
              }

              @if (msg.bookingInfo?.field === 'journeyDetails' && msg.bookingInfo?.applicable) {
                <app-chat-journey-details-input
                  [savedValue]="msg.bookingInfo.savedValue ?? null"
                  [required]="msg.bookingInfo.required"
                  [locked]="!isLastMessage"
                  (answer)="onJourneyDetailsAnswer($event)"
                  (skip)="onJourneyDetailsSkip()"
                ></app-chat-journey-details-input>
              }

              @if (
                (msg.bookingInfo?.field === 'bookingAddress' ||
                  msg.bookingInfo?.field === 'deliveryAddress' ||
                  msg.bookingInfo?.field === 'collectionAddress') &&
                msg.bookingInfo?.applicable
              ) {
                <app-chat-address-input
                  [fieldLabel]="
                    msg.bookingInfo.field === 'bookingAddress'
                      ? 'Booking address'
                      : msg.bookingInfo.field === 'deliveryAddress'
                        ? 'Delivery address'
                        : 'Collection address'
                  "
                  [savedAddresses]="msg.bookingInfo.savedAddresses ?? []"
                  [savedValue]="msg.bookingInfo.savedValue"
                  [enterpriseLocation]="msg.bookingInfo.field !== 'bookingAddress'"
                  [manualOnly]="msg.bookingInfo.field !== 'bookingAddress'"
                  [required]="msg.bookingInfo.required"
                  [showLabel]="false"
                  [locked]="!isLastMessage"
                  (answer)="onAddressAnswer(msg.bookingInfo.field, $event)"
                ></app-chat-address-input>
              }

              @if (msg.bookingInfo?.field === 'flightNumber' && msg.bookingInfo?.applicable) {
                <app-chat-flight-number-input
                  [savedValue]="msg.bookingInfo.savedValue ?? null"
                  [required]="msg.bookingInfo.required"
                  [locked]="!isLastMessage"
                  [showLabel]="false"
                  (answer)="onFlightNumberAnswer($event)"
                  (skip)="onFlightNumberSkip()"
                ></app-chat-flight-number-input>
              }

              @if (msg.bookingInfo?.field === 'driverNotes' && msg.bookingInfo?.applicable) {
                <app-chat-driver-notes-input
                  [savedValue]="msg.bookingInfo.savedValue ?? null"
                  [required]="msg.bookingInfo.required"
                  [locked]="!isLastMessage"
                  [showLabel]="false"
                  (answer)="onDriverNotesAnswer($event)"
                  (skip)="onDriverNotesSkip()"
                ></app-chat-driver-notes-input>
              }

              @if (msg.bookingSummary && (formattedTextMap[$index] || msg.text)) {
                <div class="chat-widget-message__bubble">
                  <div [innerHTML]="formattedTextMap[$index] || msg.text"></div>
                  @if (msg.role === 'assistant' && msg.id) {
                    <ng-container
                      *ngTemplateOutlet="feedbackButtons; context: { $implicit: msg }"
                    ></ng-container>
                  }
                </div>
              }

              <!-- Chatbot payment-method picker widget — same outside-bubble, -->
              <!-- locked-history behaviour as the widgets above. Not -->
              <!-- per-passenger (one selection per basket item) and no Skip -->
              <!-- — a payment method is always mandatory before booking. -->
              @if (msg.paymentMethodPrompt) {
                <app-chat-payment-input
                  [choices]="paymentMethodChoices(msg.paymentMethodPrompt)"
                  [basketItemId]="msg.paymentMethodPrompt.basketItemId"
                  [productName]="msg.paymentMethodPrompt.productName"
                  [defaultOptionId]="msg.paymentMethodPrompt.defaultOptionId"
                  [locked]="!isLastMessage"
                  [showLabel]="false"
                  (answer)="onPaymentMethodAnswer($event, msg.paymentMethodPrompt)"
                ></app-chat-payment-input>
              }

              <!-- Chatbot approver picker widget — same outside-bubble, -->
              <!-- locked-history behaviour as the widgets above. Only shown -->
              <!-- for Manual Select Approver / Multi-Level Manual Approval, -->
              <!-- one outstanding rule/level at a time; not per-passenger and -->
              <!-- no Skip — an outstanding approval level is always mandatory -->
              <!-- before booking. -->
              @if (msg.approverSelectionPrompt) {
                <app-chat-approver-input
                  [choices]="approverSelectionChoices(msg.approverSelectionPrompt)"
                  [ruleId]="msg.approverSelectionPrompt.ruleId"
                  [level]="msg.approverSelectionPrompt.level"
                  [totalLevels]="msg.approverSelectionPrompt.totalLevels"
                  [locked]="!isLastMessage"
                  [showLabel]="false"
                  (answer)="onApproverSelectionAnswer($event)"
                ></app-chat-approver-input>
              }

              <!-- Booking-result messages have no text bubble above, so their -->
              <!-- feedback controls remain in the message flow below. -->
              @if (msg.bookingResult && msg.role === 'assistant' && msg.id) {
                <ng-container
                  *ngTemplateOutlet="feedbackButtons; context: { $implicit: msg }"
                ></ng-container>
              }
            </div>

            <!-- ENT-16247 — feedback thumbs-up/down buttons, rendered in normal -->
            <!-- document flow (not absolutely positioned) so they never overlap -->
            <!-- widget controls (e.g. the phone/passport widget's Continue -->
            <!-- button) below them. -->
            <ng-template #feedbackButtons let-msg>
              <div class="chat-widget-feedback">
                <button
                  type="button"
                  class="chat-widget-feedback__btn"
                  [class.chat-widget-feedback__btn--active]="feedbackRatings[msg.id] === 'up'"
                  (click)="rateMessage(msg, 'up')"
                  aria-label="Good response"
                  i18n-aria-label="@@chatFeedbackGoodResponse"
                  title="Helpful"
                  i18n-title="@@chatFeedbackHelpfulTooltip"
                >
                  <fa-icon [icon]="faThumbsUp"></fa-icon>
                </button>
                <button
                  type="button"
                  class="chat-widget-feedback__btn"
                  [class.chat-widget-feedback__btn--active]="feedbackRatings[msg.id] === 'down'"
                  (click)="rateMessage(msg, 'down')"
                  aria-label="Bad response"
                  i18n-aria-label="@@chatFeedbackBadResponse"
                  title="Not Helpful"
                  i18n-title="@@chatFeedbackNotHelpfulTooltip"
                >
                  <fa-icon [icon]="faThumbsDown"></fa-icon>
                </button>
              </div>
            </ng-template>

            <!-- ENT-16247 — optional reason + comment panel, shown after first rating -->
            @if (msg.role === 'assistant' && msg.id && feedbackDetailOpenFor === msg.id) {
              <div class="chat-widget-feedback-detail">
                <div class="chat-widget-feedback-detail__chips">
                  @for (reason of activeFeedbackReasons; track reason) {
                    <button
                      type="button"
                      class="chat-widget-feedback-detail__chip"
                      [class.chat-widget-feedback-detail__chip--active]="
                        feedbackReasons[msg.id] === reason
                      "
                      (click)="selectFeedbackReason(msg, reason)"
                    >
                      {{ reason | feedbackReasonLabel }}
                    </button>
                  }
                </div>
                <textarea
                  class="chat-widget-feedback-detail__comment"
                  rows="2"
                  placeholder="Share details (optional)"
                  i18n-placeholder="@@chatFeedbackShareDetails"
                  [(ngModel)]="feedbackComments[msg.id]"
                ></textarea>
                <div class="chat-widget-feedback-detail__actions">
                  <button
                    type="button"
                    class="chat-widget-feedback-detail__btn chat-widget-feedback-detail__btn--ghost"
                    (click)="skipFeedbackDetail(msg)"
                    i18n="@@chatFeedbackSkip"
                  >
                    Skip
                  </button>
                  <button
                    type="button"
                    class="chat-widget-feedback-detail__btn chat-widget-feedback-detail__btn--primary"
                    (click)="submitFeedbackDetail(msg)"
                    i18n="@@chatFeedbackSubmit"
                  >
                    Submit
                  </button>
                </div>
              </div>
            }

            <!-- ENT-16247 — post-submit confirmation -->
            @if (msg.role === 'assistant' && msg.id && feedbackConfirmed[msg.id]) {
              <div class="chat-widget-feedback-confirm" i18n="@@chatFeedbackThanks">
                Thanks for your feedback.
              </div>
            }

            <!-- Cancel/keep rail journey buttons (cancel_action) -->
            @if (msg.suggestions?.length && msg.suggestions[0]?.type === 'cancel_action') {
              <div class="chat-widget-suggestions">
                @for (suggestion of msg.suggestions; track suggestion.label) {
                  <button
                    class="chat-widget-suggestion-btn"
                    (click)="onCancelAction(suggestion.action)"
                  >
                    <fa-icon
                      [icon]="suggestion.action === 'KEEP_RAIL_JOURNEY' ? faCircleCheck : faXmark"
                    ></fa-icon>
                    <span>{{ cancelActionLabels[suggestion.action] || suggestion.label }}</span>
                  </button>
                }
              </div>
            }

            <!-- Policy hints -->
            @if (msg.policyHints?.length) {
              <div class="chat-widget-policy">
                <fa-icon [icon]="faShieldCheck"></fa-icon>
                @for (hint of msg.policyHints; track hint) {
                  <span>{{ hint }}</span>
                }
              </div>
            }

            <!-- Rail results cards (RESULTS_READY state) -->
            @if (msg.railResults; as railResults) {
              <div class="chat-rail-results">
                @if (railResults.outbound.length === 0) {
                  <div class="chat-rail-no-results">
                    @if (railResults.isEuRail) {
                      <span i18n="@@chatEuRailNoResults"
                        >No EU rail options found for your search.</span
                      >
                    } @else {
                      <span i18n="@@chatRailNoResults"
                        >No journeys found for your search. Try changing your dates or
                        stations.</span
                      >
                    }
                  </div>
                } @else {
                  <ng-container
                    *ngTemplateOutlet="
                      resultsSummary;
                      context: {
                        numOfResults: railResults.outbound.length,
                        departure: railResults.parameters.departure,
                        arrival: railResults.parameters.arrival,
                        date: railSummaryMap[$index]?.summaryDate,
                        service: 'rail'
                      }
                    "
                  ></ng-container>
                  <div class="chat-rail-cheapest">
                    @if (railResults.isReturn) {
                      @if (railSummaryMap[$index]?.cheapestReturnPrice; as retPrice) {
                        <span class="chat-rail-cheapest__item"
                          >Returns from: {{ retPrice | currency: 'GBP' }}</span
                        >
                      }
                    }
                    @if (railSummaryMap[$index]?.cheapestSinglePrice; as singPrice) {
                      <span class="chat-rail-cheapest__item"
                        >Singles from: {{ singPrice | currency: 'GBP' }}</span
                      >
                    }
                  </div>

                  <!-- OUTBOUND -->
                  <div class="chat-rail-leg-header">
                    <span class="chat-rail-leg-header__label">
                      @if (railResults.isEuRail) {
                        <span i18n="@@chatEuRailOutbound">EU RAIL OUTBOUND</span>
                      } @else {
                        <span i18n="@@chatRailOutbound">OUTBOUND</span>
                      }
                    </span>
                    <span class="chat-rail-leg-header__route">
                      {{ railResults.parameters.departure }}
                      →
                      {{ railResults.parameters.arrival }}
                    </span>
                    <span class="chat-rail-leg-header__date"
                      >· {{ railResults.parameters.departureDate | date: 'EEE d MMM' }}</span
                    >
                  </div>
                  @for (card of railResults.outbound; track card.index) {
                    <app-chat-rail-card
                      [card]="card"
                      [isReturn]="false"
                      [isEuRail]="!!railResults.isEuRail"
                      [feedbackBaseId]="msg.id || ''"
                      [returnPrompt]="railReturnPromptForCard(card, msg)"
                    ></app-chat-rail-card>
                  }

                  <!-- INBOUND (return journeys only). ENT-18234 - hidden while a
                       second-leg question is live, because the outbound card is then
                       listing the same return trains and the answer belongs there. -->
                  @if (
                    railResults.isReturn &&
                    railResults.inbound?.length &&
                    !railReturnQuestionLive(msg)
                  ) {
                    <div class="chat-rail-leg-header">
                      <span class="chat-rail-leg-header__label">
                        @if (railResults.isEuRail) {
                          <span i18n="@@chatEuRailReturn">EU RAIL RETURN</span>
                        } @else {
                          <span i18n="@@chatRailReturn">RETURN</span>
                        }
                      </span>
                      <span class="chat-rail-leg-header__route">
                        {{ railResults.parameters.arrival }}
                        →
                        {{ railResults.parameters.departure }}
                      </span>
                      <span class="chat-rail-leg-header__date"
                        >· {{ railResults.parameters.returnDate | date: 'EEE d MMM' }}</span
                      >
                    </div>
                    @for (card of railResults.inbound; track card.index) {
                      <app-chat-rail-card
                        [card]="card"
                        [isReturn]="true"
                        [isEuRail]="!!railResults.isEuRail"
                        [feedbackBaseId]="msg.id || ''"
                      ></app-chat-rail-card>
                    }
                  }

                  <!-- View Full Results button -->
                  <button class="chat-rail-view-full" (click)="onViewFullResults()">
                    <fa-icon [icon]="faMagnifyingGlass"></fa-icon>
                    <span i18n="@@chatRailViewFullResults">View Full Results</span>
                  </button>
                }
              </div>
            }

            <!-- Flight results cards (RESULTS_READY state) -->
            @if (msg.flightResults; as flightResults) {
              <div class="chat-flight-results">
                <!-- No results -->
                @if (flightResults.outbound.length === 0) {
                  <div class="chat-flight-no-results" i18n="@@chatFlightNoResults">
                    No flights found for your search. Try changing your dates or airports.
                  </div>
                } @else {
                  <div class="chat-rail-leg-header">
                    <span class="chat-rail-leg-header__label" i18n="@@chatFlightOutbound"
                      >FLIGHTS</span
                    >
                    @if (flightResults.isMultiCity && flightResults.parameters.legs?.length) {
                      <span class="chat-rail-leg-header__route">
                        {{
                          flightResults.parameters.legs[0].departureCode ||
                            flightResults.parameters.legs[0].departure
                        }}
                        @for (leg of flightResults.parameters.legs; track $index) {
                          → {{ leg.arrivalCode || leg.arrival }}
                        }
                      </span>
                      <span class="chat-rail-leg-header__date"
                        >·
                        {{ flightResults.parameters.legs[0].date | date: 'EEE d MMM' }}
                        –
                        {{
                          flightResults.parameters.legs[flightResults.parameters.legs.length - 1]
                            .date | date: 'EEE d MMM'
                        }}
                      </span>
                    } @else {
                      <span class="chat-rail-leg-header__route">
                        {{
                          flightResults.parameters.departureCode ||
                            flightResults.parameters.departure
                        }}
                        →
                        {{
                          flightResults.parameters.arrivalCode || flightResults.parameters.arrival
                        }}
                      </span>
                      <span class="chat-rail-leg-header__date"
                        >·
                        {{ flightResults.parameters.departureDate | date: 'EEE d MMM' }}
                        @if (flightResults.isReturn && flightResults.parameters.returnDate) {
                          – {{ flightResults.parameters.returnDate | date: 'EEE d MMM' }}
                        }
                      </span>
                    }
                  </div>
                  @for (card of flightResults.outbound; track card.index) {
                    <app-chat-flight-card
                      [card]="card"
                      [feedbackBaseId]="msg.id || ''"
                      (fareSelect)="onFlightFareSelect($event)"
                    ></app-chat-flight-card>
                  }

                  <!-- Inbound stack (dual-singles return only). For combined-return
                   itineraries the inbound info already lives inside each
                   outbound card. Skipped entirely for multi-city. -->
                  @if (
                    !flightResults.isMultiCity &&
                    flightResults.isReturn &&
                    (flightResults.inbound?.length ?? 0) > 0
                  ) {
                    <div class="chat-rail-leg-header">
                      <span class="chat-rail-leg-header__label" i18n="@@chatFlightInbound"
                        >INBOUND</span
                      >
                      <span class="chat-rail-leg-header__route">
                        {{
                          flightResults.parameters.arrivalCode || flightResults.parameters.arrival
                        }}
                        →
                        {{
                          flightResults.parameters.departureCode ||
                            flightResults.parameters.departure
                        }}
                      </span>
                      @if (flightResults.parameters.returnDate) {
                        <span class="chat-rail-leg-header__date"
                          >· {{ flightResults.parameters.returnDate | date: 'EEE d MMM' }}</span
                        >
                      }
                    </div>
                    @for (card of flightResults.inbound; track card.index) {
                      <app-chat-flight-card
                        [card]="card"
                        [feedbackBaseId]="msg.id || ''"
                        [isInbound]="true"
                        (fareSelect)="onFlightFareSelect($event)"
                      ></app-chat-flight-card>
                    }
                  }

                  <button class="chat-rail-view-full" (click)="onFlightViewFullResults()">
                    <fa-icon [icon]="faMagnifyingGlass"></fa-icon>
                    <span i18n="@@chatFlightViewFullResults">View Full Results</span>
                  </button>
                }
              </div>
            }

            <!-- Search suggestion buttons (hidden for rail/flight/hotel — they auto-search) -->
            @if (
              msg.suggestions?.length &&
              msg.parameters &&
              msg.state !== 'HANDED_OFF' &&
              !msg.railResults &&
              !msg.flightResults &&
              !msg.hotelResults &&
              !msg.carHireResults &&
              !msg.eurostarResults &&
              !msg.taxiResults &&
              !msg.loungeResults &&
              !msg.fastTrackResults &&
              !msg.parkingResults &&
              (msg.state === 'AWAITING_CONFIRMATION' ||
                (msg.parameters?.travelType !== 'rail' &&
                  msg.parameters?.travelType !== 'flight' &&
                  msg.parameters?.travelType !== 'hotel' &&
                  msg.parameters?.travelType !== 'car-hire' &&
                  msg.parameters?.travelType !== 'eurostar' &&
                  msg.parameters?.travelType !== 'taxi' &&
                  msg.parameters?.travelType !== 'lounge' &&
                  msg.parameters?.travelType !== 'parking' &&
                  msg.parameters?.travelType !== 'fast-track'))
            ) {
              <div class="chat-widget-suggestions">
                @for (suggestion of msg.suggestions; track suggestion.label) {
                  <button
                    class="chat-widget-suggestion-btn"
                    (click)="
                      handleSuggestionButtonClick(
                        suggestion.action,
                        msg.parameters,
                        suggestion.params
                      )
                    "
                  >
                    <fa-icon
                      [icon]="
                        suggestion.action === 'FILL_AND_SEARCH' ? faMagnifyingGlass : faPenToSquare
                      "
                    ></fa-icon>
                    {{ suggestion.label }}
                  </button>
                }
              </div>
            }

            @if (msg.bookNowPrompt) {
              <div class="chat-widget-book-now">
                @if (msg.bookNowPrompt.itemLabel) {
                  <div class="basket-summary-prompt">
                    I've added <strong>{{ msg.bookNowPrompt.itemLabel }}</strong> to your basket.
                  </div>
                }
                <div class="basket-summary-prompt">Would you like me to book this now?</div>
                <div class="basket-summary-actions">
                  <button
                    type="button"
                    class="basket-summary-actions__primary"
                    (click)="confirmBookNow(msg)"
                  >
                    Yes, book it
                  </button>
                  <button
                    type="button"
                    class="basket-summary-actions__secondary"
                    (click)="declineBookNow(msg)"
                  >
                    Not yet
                  </button>
                </div>
              </div>
            }

            @if (msg.bookingSummary && !msg.bookingSummaryDismissed) {
              <div class="chat-widget-booking-flow">
                <div class="chat-widget-booking-flow__acknowledgements">
                  @if (bookingSelectionFlags[msg.id || '']?.isFlight) {
                    <label class="chat-widget-booking-flow__check">
                      <input
                        type="checkbox"
                        [checked]="bookingAcknowledgement(msg, 'identity')"
                        [disabled]="bookingStage(msg) === 'final'"
                        (change)="
                          setBookingAcknowledgement(msg, 'identity', $any($event.target).checked)
                        "
                      />
                      <span>
                        I confirm that the traveller names exactly match the passport (or other ID)
                        and that the contact mobile number is correct. I understand that changing a
                        name after booking may incur a fee.
                      </span>
                    </label>
                  }
                  <label class="chat-widget-booking-flow__check">
                    <input
                      type="checkbox"
                      [checked]="bookingAcknowledgement(msg, 'terms')"
                      [disabled]="bookingStage(msg) === 'final'"
                      (change)="
                        setBookingAcknowledgement(msg, 'terms', $any($event.target).checked)
                      "
                    />
                    @if (bookingSelectionFlags[msg.id || '']?.isRail) {
                      <span>
                        I agree to the terms and conditions and National Rail Conditions of
                        Travel.</span
                      >
                    } @else {
                      <span> I agree to the terms and conditions. </span>
                    }
                  </label>
                </div>

                @if (bookingStage(msg) === 'final') {
                  <div class="basket-summary-card">
                    <div class="basket-summary-card__header">Basket summary</div>
                    <div class="basket-summary-card__body">
                      <div class="basket-summary-card__row-header">
                        <span>Product</span>
                        <span>Price</span>
                      </div>
                      @for (item of msg.bookingSummary.items; track item.id) {
                        <div class="basket-summary-card__product-row">
                          <div class="basket-summary-card__product-info">
                            @if (item.serviceType === ServiceType.Eurostar) {
                              <i
                                class="custom_icon-eurostar basket-summary-card__product-icon"
                                aria-hidden="true"
                              ></i>
                            } @else {
                              <fa-icon
                                [icon]="bookingSummaryDisplay[item.id]?.icon"
                                class="basket-summary-card__product-icon"
                              ></fa-icon>
                            }
                            <div class="basket-summary-card__product-text">
                              <ng-container
                                *ngTemplateOutlet="
                                  bookingSummaryProduct;
                                  context: { $implicit: item }
                                "
                              ></ng-container>
                              <div class="basket-summary-card__product-subtext">
                                {{ bookingSummaryDisplay[item.id]?.dateText }}
                              </div>
                            </div>
                          </div>
                          <span class="basket-summary-card__product-price">
                            {{ item.price | currency: msg.bookingSummary.currency || 'GBP' }}
                          </span>
                        </div>
                      }

                      <div class="basket-summary-card__divider"></div>

                      <div
                        class="basket-summary-card__summary-row basket-summary-card__summary-row--total"
                      >
                        <span>Total price</span>
                        <span>{{
                          msg.bookingSummary.totalPrice
                            | currency: msg.bookingSummary.currency || 'GBP'
                        }}</span>
                      </div>
                      @if (msg.bookingSummary.totalCo2 !== undefined) {
                        <div class="basket-summary-card__summary-row">
                          <span>Total CO₂</span>
                          <span>{{ msg.bookingSummary.totalCo2 }} kg</span>
                        </div>
                      }
                      @if (msg.bookingSummary.paymentMethod) {
                        <div class="basket-summary-card__summary-row">
                          <span>Payment</span>
                          <span>{{
                            msg.bookingSummary.paymentMethod?.name || 'Not selected'
                          }}</span>
                        </div>
                      }
                    </div>
                  </div>

                  <div class="basket-summary-prompt">Would you like me to confirm the booking?</div>

                  <div class="basket-summary-actions">
                    <button
                      type="button"
                      class="basket-summary-actions__primary"
                      [disabled]="bookingConfirmationInProgress(msg)"
                      (click)="confirmBooking(msg)"
                    >
                      Yes, confirm
                    </button>
                    <button
                      type="button"
                      class="basket-summary-actions__secondary"
                      (click)="declineBooking(msg)"
                    >
                      Not yet
                    </button>
                  </div>
                } @else {
                  <button
                    type="button"
                    class="chat-widget-suggestion-btn chat-widget-booking-flow__continue"
                    [disabled]="
                      !bookingAcknowledgement(msg, 'terms') ||
                      (bookingSelectionFlags[msg.id || '']?.isFlight &&
                        !bookingAcknowledgement(msg, 'identity'))
                    "
                    (click)="continueBooking(msg)"
                  >
                    Continue
                  </button>
                }
              </div>
            }

            @if (msg.bookingResult) {
              @if (msg.bookingResult.success && msg.bookingResult.summary) {
                <div class="booking-confirmation-card">
                  <div class="booking-confirmation-card__header">
                    <fa-icon [icon]="faCircleCheck"></fa-icon>
                    <span>BOOKING CONFIRMED</span>
                  </div>

                  @if (msg.bookingResult.reference) {
                    <div class="booking-confirmation-card__reference">
                      <span>Booking reference</span>
                      <strong>{{ msg.bookingResult.reference }}</strong>
                    </div>
                    <div class="booking-confirmation-card__divider"></div>
                  }

                  <div class="booking-confirmation-card__details">
                    <div class="booking-confirmation-card__row-header">
                      <span>Product</span>
                      <span>Price</span>
                    </div>
                    @for (item of msg.bookingResult.summary.items; track item.id) {
                      <div class="booking-confirmation-card__item">
                        <div class="booking-confirmation-card__item-info">
                          @if (item.serviceType === ServiceType.Eurostar) {
                            <i
                              class="custom_icon-eurostar booking-confirmation-card__item-icon"
                              aria-hidden="true"
                            ></i>
                          } @else {
                            <fa-icon
                              [icon]="bookingSummaryDisplay[item.id]?.icon"
                              class="booking-confirmation-card__item-icon"
                            ></fa-icon>
                          }
                          <div class="booking-confirmation-card__item-text">
                            <ng-container
                              *ngTemplateOutlet="
                                bookingSummaryProduct;
                                context: { $implicit: item }
                              "
                            ></ng-container>
                            <span class="basket-summary-card__product-subtext">
                              {{ bookingSummaryDisplay[item.id]?.dateText }}
                            </span>
                          </div>
                        </div>
                        <strong class="booking-confirmation-card__price">
                          {{ item.price | currency: msg.bookingResult.summary.currency || 'GBP' }}
                        </strong>
                      </div>
                    }
                  </div>

                  <div class="booking-confirmation-card__divider"></div>
                  <div class="booking-confirmation-card__footer">
                    <span>Total paid</span>
                    <strong>
                      {{
                        msg.bookingResult.summary.totalPrice
                          | currency: msg.bookingResult.summary.currency || 'GBP'
                      }}
                    </strong>
                  </div>
                </div>
              } @else if (msg.bookingResult.status === 'AWAITING_APPROVAL') {
                <div class="chat-widget-booking-flow__summary">
                  <span i18n="@@chatAwaitingApprovalSummary">
                    Your products have been sent for approval.
                  </span>
                </div>
              } @else {
                <div class="chat-widget-booking-flow__summary">
                  @if (msg.bookingResult.success) {
                    <strong>Booking references</strong>
                    @for (item of msg.bookingResult.items || []; track item.id) {
                      <span>{{
                        item.pnrLocator || item.bookingThirdPartyReference || item.id
                      }}</span>
                    }
                    @if (msg.bookingResult.reference) {
                      <span>Reference: {{ msg.bookingResult.reference }}</span>
                    }
                  } @else {
                    <span>{{
                      msg.bookingResult.error || 'The products remain in your basket.'
                    }}</span>
                  }
                </div>
              }
            }

            <!-- Approval buttons (HANDED_OFF / awaiting approval) -->
            @if (msg.state === 'HANDED_OFF' && msg.suggestions?.length) {
              <div class="chat-widget-suggestions">
                @for (suggestion of msg.suggestions; track suggestion.label) {
                  <button
                    class="chat-widget-suggestion-btn"
                    (click)="onApproval(suggestion.action)"
                  >
                    <fa-icon
                      [icon]="suggestion.action === 'APPROVE_BOOKING' ? faCircleCheck : faXmark"
                    ></fa-icon>
                    {{ suggestion.label }}
                  </button>
                }
              </div>
            }

            <!-- Continue button under the automatic rail seat-reservation message -->
            @if (
              msg.suggestions?.length &&
              msg.suggestions[0].action === 'RAIL_SEAT_RESERVATION_CONTINUE' &&
              !msg.railSeatReservationPrompt
            ) {
              <div class="chat-widget-suggestions">
                <button
                  class="chat-widget-suggestion-btn"
                  (click)="onRailSeatReservationContinue()"
                >
                  {{ msg.suggestions[0].label }}
                </button>
              </div>
            }

            <!-- ENT-16431 — MI (cost centre) collection -->
            @if (msg.state === 'AWAITING_MI' && msg.mi) {
              <!-- Locked/defaulted values ride along on whichever interrupt pauses
                   first; show them once as plain description text, above the
                   active question or the recap. -->
              @if (msg.mi.lockedDefaults?.length) {
                <div class="chat-mi-locked-defaults">
                  @for (field of msg.mi.lockedDefaults; track field.name) {
                    <div class="chat-mi-locked-defaults__row">
                      <app-chat-mi-field-label
                        [name]="field.name"
                        [mandatory]="field.mandatory"
                        [description]="field.description"
                        [locked]="true"
                      ></app-chat-mi-field-label>
                      <span class="chat-mi-locked-defaults__value">{{ field.value || '—' }}</span>
                    </div>
                  }
                </div>
              }
              @switch (msg.mi.kind) {
                @case ('mi-recap') {
                  <app-chat-mi-recap
                    [fields]="msg.mi.fields ?? []"
                    (confirm)="onMiConfirm()"
                    (editField)="onMiFieldEdit($event)"
                  ></app-chat-mi-recap>
                }
                @default {
                  @if (msg.mi.field) {
                    <app-chat-mi-input
                      [field]="msg.mi.field"
                      [selectedValue]="msg.mi.selectedValue"
                      [required]="msg.mi.required"
                      [error]="msg.mi.error"
                      (answer)="onMiAnswer($event)"
                    ></app-chat-mi-input>
                  }
                }
              }
            }

            <!-- WIP: inline seat/extras chat card (`app-chat-flight-extras-card`)
                 isn't polished enough yet — commented out for now. The modal
                 is still skipped for chatbot bookings (see
                 `ChatbotService.presentFlightExtras`'s `flightExtrasCardEnabled`
                 flag, which currently auto-resolves as "Skip" instead of
                 emitting `flightExtrasRequest`). Re-enable both together. -->
            <!--
            @if (msg.flightExtrasRequest; as extrasRequest) {
              <app-chat-flight-extras-card
                [request]="extrasRequest"
                (resolve)="onFlightExtrasResolve(extrasRequest.id, $event)"
              ></app-chat-flight-extras-card>
            }
            -->

            <!-- ENT-16311 - travelcard choice for a London-bound rail add. Unlike
                 the flight extras card above this one is live: it is three
                 buttons, not a seat map, and the add-to-basket behind it is
                 parked until it is answered. -->
            @if (msg.railTravelcardsRequest; as travelcardsRequest) {
              <app-chat-rail-travelcards-card
                [request]="travelcardsRequest"
                (resolve)="onRailTravelcardsResolve(travelcardsRequest.id, $event)"
              ></app-chat-rail-travelcards-card>
            }

            <!-- Selection confirmation -->
            @if (msg.action === 'select') {
              <div class="chat-widget-selection">
                <fa-icon [icon]="faCircleCheck"></fa-icon>
                <span i18n="@@chatWidgetSelectionApplied">Selection applied</span>
              </div>
            }

            <!-- Alternative suggestion -->
            @if (msg.alternativeIndex !== undefined && msg.alternativeIndex >= 0) {
              <div class="chat-widget-alternative">
                <button
                  class="chat-widget-suggestion-btn"
                  (click)="onSelectAlternative(msg.alternativeIndex)"
                >
                  <fa-icon [icon]="faShieldCheck"></fa-icon>
                  <ng-container i18n="@@chatWidgetSelectAlternative"
                    >Select in-policy alternative instead</ng-container
                  >
                </button>
              </div>
            }

            <!-- Hotel result cards -->
            @if (msg.hotelResults; as hotelResults) {
              <div class="chat-widget-hotel-results">
                <div class="chat-widget-hotel-header">
                  <strong i18n="@@chatWidgetHotelsHeading">HOTELS</strong>
                  @if (hotelResults.searchHeader) {
                    <span> {{ hotelResults.searchHeader }}</span>
                  }
                </div>
                @if (hotelResults.appliedFilters?.length) {
                  <div class="chat-widget-hotel-filters">
                    @for (chip of hotelResults.appliedFilters; track $index) {
                      <span class="chat-widget-hotel-filter-chip">{{ chip.label }}</span>
                    }
                  </div>
                }
                @for (card of hotelResults.cards; track card.index) {
                  <app-chat-hotel-card
                    [card]="card"
                    [feedbackBaseId]="msg.id || ''"
                    (hotelNameClick)="onHotelNameClick($event)"
                    (roomSelect)="onHotelRoomSelect($event)"
                  ></app-chat-hotel-card>
                }
                <button
                  class="chat-widget-view-results-btn"
                  (click)="onViewHotelResults()"
                  i18n="@@chatWidgetViewFullResults"
                >
                  View Full Results
                </button>
              </div>
            }

            <!-- Car hire result cards -->
            @if (msg.carHireResults; as carHireResults) {
              <div class="chat-widget-car-hire-results">
                <div class="chat-widget-hotel-header">
                  <strong i18n="@@chatCarHireHeader">CAR HIRE</strong>
                  @if (carHireResults.searchHeader) {
                    <span> {{ carHireResults.searchHeader }}</span>
                  }
                </div>
                @for (card of carHireResults.cards; track card.index) {
                  <app-chat-car-hire-card
                    [card]="card"
                    (carSelect)="onCarHireCardSelect($event)"
                  ></app-chat-car-hire-card>
                }
                <button
                  class="chat-widget-view-results-btn"
                  (click)="onViewCarHireResults()"
                  i18n="@@chatCarHireViewFull"
                >
                  View Full Results
                </button>
              </div>
            }

            <!-- Depot selection list -->
            @if (msg.depotChoices?.length) {
              <app-chat-depot-list
                [depots]="msg.depotChoices"
                (depotSelect)="onDepotSelect($event)"
              ></app-chat-depot-list>
            }

            <!-- Taxi result cards -->
            @if (msg.taxiResults; as taxiResults) {
              <div class="chat-taxi-results">
                @if (taxiResults.cards.length === 0) {
                  <div class="chat-rail-no-results" i18n="@@chatTaxiNoResults">
                    No taxi options found for your search.
                  </div>
                } @else {
                  <p class="chat-rail-summary">
                    <span i18n="@@chatHereAre">Here are the</span>
                    {{ taxiResults.cards.length }}
                    @if (taxiResults.cards.length === 1) {
                      <span i18n="@@chatTaxiResultSingular">taxi result </span>
                    } @else {
                      <span i18n="@@chatTaxiResultPlural">taxi results </span>
                    }
                    <span i18n="@@chatFor">for</span> {{ taxiResults.parameters.departure }}
                    <span i18n="@@chatTo">to</span> {{ taxiResults.parameters.arrival }}
                    <span i18n="@@chatOn">on</span>
                    {{ taxiResults.parameters.departureDate | date: 'EEEE d MMM' }}
                    @if (taxiResults.parameters.departureTime) {
                      <span i18n="@@chatAt"> at </span>{{ taxiResults.parameters.departureTime }}
                    }
                    .
                    <span i18n="@@chatTaxiSummaryAction"
                      >To view full results and book, click
                      <strong>View Full Results</strong>.</span
                    >
                  </p>

                  <div class="chat-rail-leg-header">
                    <span class="chat-rail-leg-header__label" i18n="@@chatTaxiHeader">TAXIS</span>
                    <span class="chat-rail-leg-header__route">
                      <span class="chat-rail-leg-header__route-loc">{{
                        taxiResults.parameters.departure
                      }}</span>
                      →
                      <span class="chat-rail-leg-header__route-loc">{{
                        taxiResults.parameters.arrival
                      }}</span>
                    </span>
                    <span class="chat-rail-leg-header__date chat-rail-leg-header__date--taxi">
                      · {{ taxiResults.parameters.departureDate | date: 'EEE d MMM' }}
                    </span>
                  </div>
                  @for (card of taxiResults.cards; track card.index) {
                    <app-chat-taxi-card
                      [card]="card"
                      (cardClick)="onTaxiCardSelect(card)"
                    ></app-chat-taxi-card>
                  }
                  <button class="chat-rail-view-full" (click)="onTaxiViewFullResults()">
                    <fa-icon [icon]="faMagnifyingGlass"></fa-icon>
                    <span i18n="@@chatTaxiViewFull">View Full Results</span>
                  </button>
                }
              </div>
            }

            <!-- Eurostar result cards -->
            @if (msg.eurostarResults; as eurostarResults) {
              <div class="chat-widget-eurostar-results">
                @if (eurostarResults.outbound.length > 0) {
                  <ng-container
                    *ngTemplateOutlet="
                      resultsSummary;
                      context: {
                        numOfResults: eurostarResults.outbound.length,
                        departure: eurostarResults.parameters.departure,
                        arrival: eurostarResults.parameters.arrival,
                        date: eurostarSummaryDateMap[$index],
                        service: 'eurostar'
                      }
                    "
                  ></ng-container>
                }
                @if (eurostarResults.outbound.length === 0) {
                  <p class="chat-widget-no-results" i18n="@@chatEurostarNoResults">
                    No Eurostar journeys found. Try different dates or stations.
                  </p>
                } @else {
                  <!-- OUTBOUND -->
                  <div class="chat-eurostar-leg-header">
                    <span class="chat-eurostar-leg-header__label" i18n="@@chatEurostarOutbound"
                      >OUTBOUND</span
                    >
                    <span class="chat-eurostar-leg-header__route">
                      {{ eurostarResults.parameters.departure }}
                      →
                      {{ eurostarResults.parameters.arrival }}
                    </span>
                    <span class="chat-eurostar-leg-header__date"
                      >· {{ eurostarResults.parameters.departureDate | date: 'EEE d MMM' }}</span
                    >
                  </div>
                  @for (card of eurostarResults.outbound; track card.index) {
                    <app-chat-eurostar-card
                      [card]="card"
                      [from]="eurostarResults.parameters.departure"
                      [to]="eurostarResults.parameters.arrival"
                      [departDate]="eurostarResults.parameters.departureDate | date: 'EEE d MMM y'"
                      (fareSelect)="onEurostarFareSelect($event, 'outbound')"
                    ></app-chat-eurostar-card>
                  }

                  <!-- RETURN (return journeys only) -->
                  @if (eurostarResults.isReturn && eurostarResults.inbound?.length) {
                    <div class="chat-eurostar-leg-header">
                      <span class="chat-eurostar-leg-header__label" i18n="@@chatEurostarReturn"
                        >RETURN</span
                      >
                      <span class="chat-eurostar-leg-header__route">
                        {{ eurostarResults.parameters.arrival }}
                        →
                        {{ eurostarResults.parameters.departure }}
                      </span>
                      <span class="chat-eurostar-leg-header__date"
                        >· {{ eurostarResults.parameters.returnDate | date: 'EEE d MMM' }}</span
                      >
                    </div>
                    @for (card of eurostarResults.inbound; track card.index) {
                      <app-chat-eurostar-card
                        [card]="card"
                        [from]="eurostarResults.parameters.arrival"
                        [to]="eurostarResults.parameters.departure"
                        [departDate]="eurostarResults.parameters.returnDate | date: 'EEE d MMM y'"
                        (fareSelect)="onEurostarFareSelect($event, 'inbound')"
                      ></app-chat-eurostar-card>
                    }
                  }
                }
                <button
                  class="chat-widget-view-results-btn"
                  (click)="onEurostarViewFullResults()"
                  i18n="@@chatEurostarViewFull"
                >
                  View Full Results
                </button>
              </div>
            }

            <!-- Upcoming trips (ENT-16197) -->
            @if (msg.upcomingBookings?.length) {
              <div class="chat-widget-upcoming-trips">
                @for (booking of msg.upcomingBookings; track booking.basketId) {
                  <app-chat-trip-card [trip]="booking"></app-chat-trip-card>
                }
                @if (msg.hasMoreBookings) {
                  <p class="chat-widget-upcoming-trips__more-prompt" i18n="@@chatTripsMorePrompt">
                    You have more upcoming bookings. Would you like to see more?
                  </p>
                  <button
                    class="chat-widget-suggestion-btn"
                    (click)="onShowMoreBookings()"
                    i18n="@@chatTripsShowMore"
                  >
                    <fa-icon [icon]="faChevronRight"></fa-icon>
                    Show more
                  </button>
                }
                <button
                  class="chat-widget-view-results-btn"
                  (click)="onViewMyBookings(msg)"
                  i18n="@@chatTripsViewMyBookings"
                >
                  View My Bookings
                </button>
              </div>
            }

            <!-- Lounge result cards -->
            @if (msg.loungeResults; as loungeResults) {
              <div class="chat-lounge-results">
                @if (loungeResults.cards.length === 0) {
                  <p class="chat-lounge-no-results" i18n="@@chatLoungeNoResults">
                    No lounge options found for your search.
                  </p>
                } @else {
                  <p class="chat-lounge-summary">
                    <span i18n="@@chatHereAre">Here are the</span>
                    {{ loungeResults.cards.length }}
                    @if (loungeResults.cards.length === 1) {
                      <span i18n="@@chatLoungeResultSingular">lounge result </span>
                    } @else {
                      <span i18n="@@chatLoungeResultPlural">lounge results </span>
                    }
                    <span i18n="@@chatFor">for</span>
                    {{ loungeResults.searchHeader }}.
                    <span i18n="@@chatLoungeSummaryAction"
                      >Click <strong>View Full Results</strong> to see all available options.</span
                    >
                  </p>
                  <div class="chat-lounge-header">
                    <strong i18n="@@chatWidgetLoungesHeading">LOUNGES</strong>
                    @if (loungeResults.searchHeader) {
                      <span> {{ loungeResults.searchHeader }}</span>
                    }
                  </div>
                  @for (card of loungeResults.cards; track card.index) {
                    <app-chat-lounge-card
                      [card]="card"
                      [feedbackBaseId]="msg.id || ''"
                      (cardClick)="onLoungeCardSelect(card)"
                    ></app-chat-lounge-card>
                  }
                  <button class="chat-rail-view-full" (click)="onLoungeViewFullResults()">
                    <fa-icon [icon]="faMagnifyingGlass"></fa-icon>
                    <span i18n="@@chatLoungeViewFull">View Full Results</span>
                  </button>
                }
              </div>
            }

            <!-- Fast track result cards -->
            @if (msg.fastTrackResults; as fastTrackResults) {
              <div class="chat-lounge-results">
                @if (fastTrackResults.cards.length === 0) {
                  <p class="chat-lounge-no-results" i18n="@@chatFastTrackNoResults">
                    No fast track options found for your search.
                  </p>
                } @else {
                  <p class="chat-lounge-summary">
                    <span i18n="@@chatHereAre">Here are the</span>
                    {{ fastTrackResults.cards.length }}
                    @if (fastTrackResults.cards.length === 1) {
                      <span i18n="@@chatFastTrackResultSingular">fast track result </span>
                    } @else {
                      <span i18n="@@chatFastTrackResultPlural">fast track results </span>
                    }
                    <span i18n="@@chatFor">for</span>
                    {{ fastTrackResults.searchHeader }}.
                    <span i18n="@@chatFastTrackSummaryAction"
                      >Click <strong>View Full Results</strong> to see all available options.</span
                    >
                  </p>
                  @for (card of fastTrackResults.cards; track card.index) {
                    <app-chat-fast-track-card
                      [card]="card"
                      (cardClick)="onFastTrackCardSelect(card)"
                    ></app-chat-fast-track-card>
                  }
                  <button class="chat-rail-view-full" (click)="onFastTrackViewFullResults()">
                    <fa-icon [icon]="faMagnifyingGlass"></fa-icon>
                    <span i18n="@@chatFastTrackViewFull">View Full Results</span>
                  </button>
                }
              </div>
            }

            <!-- Parking result cards -->
            @if (msg.parkingResults; as parkingResults) {
              <div class="chat-parking-results">
                @if (parkingResults.cards.length === 0) {
                  <p class="chat-parking-no-results" i18n="@@chatParkingNoResults">
                    No parking options found for your search.
                  </p>
                } @else {
                  <p class="chat-parking-summary">
                    <span i18n="@@chatHereAre">Here are the</span>
                    {{ parkingResults.cards.length }}
                    @if (parkingResults.cards.length === 1) {
                      <span i18n="@@chatParkingResultSingular">parking result </span>
                    } @else {
                      <span i18n="@@chatParkingResultPlural">parking results </span>
                    }
                    <span i18n="@@chatFor">for</span>
                    {{ parkingResults.searchHeader }}.
                    <span i18n="@@chatParkingSummaryAction"
                      >Click <strong>View Full Results</strong> to see all available options.</span
                    >
                  </p>
                  <!-- ENT-16273: result-card header (Parking · location · dates), mirrors the
                       hotel header. The per-card date range moves up here so each card can lead
                       with the car-park name. -->
                  <div class="chat-parking-header">
                    <strong i18n="@@chatParkingHeader">PARKING</strong>
                    @if (parkingResults.searchHeader) {
                      <span> {{ parkingResults.searchHeader }}</span>
                    }
                  </div>
                  @for (card of parkingResults.cards; track card.index) {
                    <app-chat-parking-card
                      [card]="card"
                      (cardClick)="onParkingCardSelect(card)"
                    ></app-chat-parking-card>
                  }
                  <button class="chat-rail-view-full" (click)="onParkingViewFullResults()">
                    <fa-icon [icon]="faMagnifyingGlass"></fa-icon>
                    <span i18n="@@chatParkingViewFull">View Full Results</span>
                  </button>
                }
              </div>
            }
          </div>
        }

        <!-- Typing / searching indicator -->
        @if (isLoading || isSearching) {
          <div class="chat-widget-message chat-widget-message--assistant">
            <div class="chat-widget-message__bubble chat-widget-typing">
              <span class="chat-widget-typing__dot"></span>
              <span class="chat-widget-typing__dot"></span>
              <span class="chat-widget-typing__dot"></span>
              @if (isSearching && searchingTravelType === 'flight') {
                <span class="chat-widget-typing__label" i18n="@@chatFlightSearchingFlights"
                  >Searching for flights…</span
                >
              } @else if (isSearching) {
                <span class="chat-widget-typing__label">{{ searchingLabel }}</span>
              }
            </div>
          </div>
        }
      </div>

      <!-- Input -->
      <div class="chat-widget-input">
        <textarea
          #messageInput
          [(ngModel)]="inputText"
          (keydown)="onKeydown($event)"
          [placeholder]="isListening ? listeningPlaceholder : askAnythingPlaceholder"
          rows="2"
          [disabled]="isLoading"
        ></textarea>
        @if (isVoiceSupported) {
          <button
            type="button"
            class="chat-widget-mic"
            [class.listening]="isListening"
            (click)="toggleVoice()"
            [disabled]="isLoading"
            [attr.aria-label]="isListening ? stopListeningLabel : voiceInputLabel"
            [attr.aria-pressed]="isListening"
          >
            <fa-icon [icon]="isListening ? faMicrophoneSlash : faMicrophone"></fa-icon>
          </button>
        }
        <button
          class="chat-widget-send"
          (click)="sendMessage()"
          [disabled]="!inputText.trim() || isLoading"
          i18n-aria-label="@@chatWidgetSendMessage"
          aria-label="Send message"
        >
          <fa-icon [icon]="faPaperPlaneTop"></fa-icon>
        </button>
      </div>
    </div>
  }
}

<!-- Fixed-position tooltip — rendered outside all overflow:hidden containers -->
@if (tooltipVisible) {
  <div class="chat-btn-tooltip" [style.left.px]="tooltipX" [style.top.px]="tooltipY">
    {{ tooltipText }}
  </div>
}

<!-- Shared results summary line (rail + eurostar). -->
<ng-template
  #resultsSummary
  let-numOfResults="numOfResults"
  let-departure="departure"
  let-arrival="arrival"
  let-date="date"
  let-service="service"
>
  <p class="chat-rail-summary">
    <span i18n="@@chatHereAre">Here are the</span>
    {{ numOfResults }}
    @switch (service) {
      @case ('eurostar') {
        @if (numOfResults === 1) {
          <span i18n="@@chatEurostarResultSingular">Eurostar result</span>
        } @else {
          <span i18n="@@chatEurostarResultPlural">Eurostar results</span>
        }
      }
      @default {
        @if (numOfResults === 1) {
          <span i18n="@@chatRailResultSingular">rail result</span>
        } @else {
          <span i18n="@@chatRailResultPlural">rail results</span>
        }
      }
    }
    {{ ' ' }}<span i18n="@@chatFor">for</span>
    {{ departure }}
    <span i18n="@@chatTo">to</span>
    {{ arrival }}
    <span i18n="@@chatOn">on</span>
    {{ date }}.
    <span i18n="@@chatRailSummaryAction"
      >Click the result card to see available fares or to view full results and book, click
      <strong>View Full Results</strong>.</span
    >
  </p>
</ng-template>

<!-- Shared product details for basket and booking-confirmation summaries. -->
<ng-template #bookingSummaryProduct let-item>
  @if (
    (item.serviceType === ServiceType.Flight || item.serviceType == ServiceType.Rail) &&
    item.segments?.length
  ) {
    @for (segment of item.segments; track $index) {
      <span class="basket-summary-card__product-name">
        {{ segment.origin }} → {{ segment.destination }}
      </span>
    }
    <div class="basket-summary-card__flight-numbers">
      <span class="basket-summary-card__flight-number">
        {{ bookingSummaryDisplay[item.id]?.listCarriers || '' }}
      </span>
    </div>
  } @else {
    <span class="basket-summary-card__product-name">
      {{ bookingSummaryDisplay[item.id]?.productText | splitCamelCase | translate }}
    </span>
  }
</ng-template>
